import { useEffect, useMemo, useState } from "react";

import {
  useGetAnalysisSummaryByDataset,
  useGetSchemaDatasetByAnalysis
} from "@/api/analysis/useAnalysisAPI";
import { useGetUserProfileAPI } from "@/api/userProfile/useUserProfileAPI";
import { CATEGORY_OPTIONS } from "@/components/analysisSummary/filterByGrid/constants";
import { DatasetSectionViewType } from "@/components/analysisSummary/filterByGrid/datasetSection";
import {
  AnalysisConfiguration,
  ECategoryTransform
} from "@/components/analysisSummary/filterByGrid/types";
import {
  getDatasetSummarySortByOptions,
  getDefaultAnalysisConfig,
  getDefaultViewType,
  getFilteredGrids
} from "@/components/analysisSummary/filterByGrid/utils";
import { IOptionItem } from "@/components/ui/dropdown/types";
import { DROPDOWN_ALL_VALUE } from "@/components/ui/dropdownMultiSelect";
import { DATA_TYPE_VALUES, LIMITED_DATA_SUMMARY } from "@/constants/dataset";
import { useAnalysisSummaryDataContext } from "@/contexts/AnalysisSummaryDataContext";
import { capitalizeFirst } from "@/helpers/string";
import { useDebounce } from "@/hooks/useDebounce";
import { DatasetData, DatasetDataType } from "@/types/dataset";
import { isOrdinalType } from "@/utils/dataset";

import useToast, { EToastType } from "../ui/useToast";
import useAnalysisSummaryVisualization from "./useAnalysisSummaryVisualization";

export function useAnalysisDatasetSummary(
  datasetData: DatasetData,
  latestAnalysisId: string | undefined,
  gridsSelected: string[]
) {
  const { showToast } = useToast();
  const [analysisConfiguration, setAnalysisConfiguration] =
    useState<AnalysisConfiguration | null>(null);
  const [prevDatasetId, setPrevDatasetId] = useState<string | null>(null);
  const [viewTypeOverride, setViewTypeOverride] =
    useState<DatasetSectionViewType | null>(null);
  const { gridValue, registerCategoryNormalizer } =
    useAnalysisSummaryDataContext();

  const { data: userProfile } = useGetUserProfileAPI();

  const debouncedGridsSelected = useDebounce(gridsSelected, 500);

  const filteredGrids = getFilteredGrids(
    analysisConfiguration?.category?.value,
    debouncedGridsSelected,
    gridValue
  );

  // Fetch data
  const {
    isLoading: isLoadingDatasetSummary,
    data: summaryData,
    error: analysisSummaryError
  } = useGetAnalysisSummaryByDataset(
    datasetData.dataset_id,
    latestAnalysisId,
    analysisConfiguration,
    filteredGrids
  );

  const { data: datasetSchema, error: datasetSchemaError } =
    useGetSchemaDatasetByAnalysis(latestAnalysisId, datasetData.dataset_id);

  // Numeric column check
  const hasNumericColumn = useMemo(
    () =>
      datasetData.columns.some(
        (col) =>
          !isOrdinalType(DATA_TYPE_VALUES[DatasetDataType[col.data_type]])
      ),
    [datasetData.columns]
  );

  const highestScoreGridId = useMemo(() => {
    const isAllSelected = gridsSelected?.[0] === DROPDOWN_ALL_VALUE;
    const filteredGridValue = isAllSelected
      ? gridValue
      : gridValue.filter((grid) => gridsSelected?.includes(grid.value));

    if (filteredGridValue.length === 0) return "";

    const highestScoreGrid = filteredGridValue.reduce(
      (prev, current) => (prev.score > current.score ? prev : current),
      filteredGridValue[0]
    );

    return highestScoreGrid.value;
  }, [gridValue, gridsSelected]);

  const { columns, rows, donutChartData, barChartData } =
    useAnalysisSummaryVisualization(
      summaryData,
      highestScoreGridId,
      userProfile?.timezone
    );

  const shownBarChartData = barChartData.slice(
    0,
    LIMITED_DATA_SUMMARY.BAR_CHART
  );

  const shownDonutChartData = donutChartData.slice(
    0,
    LIMITED_DATA_SUMMARY.DONUT_CHART
  );

  if (donutChartData.length > LIMITED_DATA_SUMMARY.DONUT_CHART) {
    const remainingItems = donutChartData.slice(
      LIMITED_DATA_SUMMARY.DONUT_CHART
    );

    const othersValue = remainingItems.reduce((totalValue, currentItem) => {
      const value = currentItem?.value;
      return typeof value === "number" && !Number.isNaN(value)
        ? totalValue + value
        : totalValue;
    }, 0);

    const othersEntry = { name: "Others", value: othersValue };
    shownDonutChartData.splice(
      LIMITED_DATA_SUMMARY.DONUT_CHART - 1,
      1,
      othersEntry
    );
  }

  // Get sort by options based on the current method and selected grids
  const sortByOptions = useMemo(
    () =>
      getDatasetSummarySortByOptions(
        analysisConfiguration,
        debouncedGridsSelected,
        gridValue
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      analysisConfiguration?.method,
      analysisConfiguration?.category,
      debouncedGridsSelected,
      gridValue
    ]
  );

  const attributesOptions: IOptionItem[] = useMemo(() => {
    return (
      datasetSchema?.schema
        ?.filter(
          (schema) => schema.attribute_name !== "Grid ID" && schema.is_visible
        )
        ?.map((item) => ({
          label: item.attribute_name,
          value: item.id
        })) || []
    );
  }, [datasetSchema]);

  const defaultViewType = useMemo<DatasetSectionViewType>(() => {
    if (!attributesOptions.length || !datasetSchema || !datasetData) {
      return DatasetSectionViewType.TABLE;
    }
    return getDefaultViewType(datasetData, attributesOptions, datasetSchema);
  }, [datasetData, attributesOptions, datasetSchema]);

  const viewType = viewTypeOverride ?? defaultViewType;

  // Initialize analysis configuration once per dataset, after the schema arrives.
  // Tracked via prevDatasetId state so it only re-runs when the dataset changes,
  // While still reading the latest grid state directly during render.
  if (datasetSchema && prevDatasetId !== datasetData.dataset_id) {
    setPrevDatasetId(datasetData.dataset_id);

    const newConfig = getDefaultAnalysisConfig(
      datasetData,
      hasNumericColumn,
      attributesOptions
    );

    if (Object.keys(newConfig).length === 0) {
      setAnalysisConfiguration(newConfig);
    } else {
      const correctSortByOptions = getDatasetSummarySortByOptions(
        newConfig,
        gridsSelected,
        gridValue
      );
      setAnalysisConfiguration({
        ...newConfig,
        sortBy: correctSortByOptions[0]
      });
    }
  }

  useEffect(() => {
    if (
      !analysisSummaryError?.message?.message &&
      !datasetSchemaError?.message.message
    )
      return;
    showToast({
      type: EToastType.ERROR,
      message: capitalizeFirst(
        analysisSummaryError?.message?.message ||
          datasetSchemaError?.message?.message
      )
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analysisSummaryError, datasetSchemaError]);

  /*
   * Register a category normalizer that FilterByAnalysisArea.handleSelectGrid
   * invokes when grids change to ALL. Keeps the side-effect tied to the user
   * action instead of a reactive useEffect on derived state.
   */
  useEffect(() => {
    return registerCategoryNormalizer(datasetData.dataset_id, () => {
      setAnalysisConfiguration((prev) => {
        if (
          prev?.category?.value !== ECategoryTransform.DISPLAY_ROWS_SERIES ||
          prev.category === CATEGORY_OPTIONS[0]
        )
          return prev;
        return { ...prev, category: CATEGORY_OPTIONS[0] };
      });
    });
  }, [datasetData.dataset_id, registerCategoryNormalizer]);

  return {
    isLoadingDatasetSummary,
    summaryData,
    columns,
    rows,
    verticalBarChartData: barChartData,
    donutChartData: shownDonutChartData,
    barChartData: shownBarChartData,
    analysisConfiguration,
    viewType,
    attributesOptions,
    debouncedGridsSelected,
    sortByOptions,
    setAnalysisConfiguration,
    setViewTypeOverride,
    hasNumericColumn,
    datasetSchema
  };
}
